<template>
  <div class="file-count">
    <div class="flex items-center justify-end gap-x-1 select-none" :class="attrsClass">
      <slot name="prepend" />
      <PButton
        v-if="!hideFileCount && (fileCollection.length > 0 || isUploading)"
        ref="fileCountButton"
        class="min-w-5 justify-center"
        :color="isActive ? 'primary-lighten-1' : 'primary'"
        :size="size"
        :variant="variant"
        :disabled="fileCollection.length === 0"
        :loading="isLoading || !windowStore.allWindowsLoaded"
        :icon="$slots.prepend ? undefined : ['fas', 'paperclip']"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @click.stop="onFileCountClick"
        title="Bekijk de bijlage, klik om direct te openen"
        tabindex="-1"
      >
        <div v-if="windowStore.allWindowsLoaded">
          <slot />
          {{ fileCollection.length }}
        </div>
      </PButton>
      <PProgress
        v-if="!hideFileCount && isUploading"
        size="sm"
        class="w-12! shrink-0"
        aria-label="Bestand wordt geüpload"
      />
      <slot name="append" />
    </div>

    <FilePreviewList
      v-if="isPreviewMode"
      v-model="fileCollection"
      class="pointer-events-auto absolute inset-0 -top-2.5! -right-2.5! z-40"
      :container-class="previewClass"
      ref="previewContainer"
      :is-uploading="isUploading"
      :drag-default-is-copy="dragDefaultIsCopy"
      @close="disablePreviewMode"
      @mouseenter="onPreviewMouseEnter"
      @leave="onPreviewMouseLeave"
      @open="isContextMenuOpen = $event"
      :on-delete="onDelete"
      :on-move="onMove"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted, useAttrs, type HTMLAttributes } from "vue"
import { storeToRefs } from "pinia"
import FilePreviewList from "@/components/file/FilePreviewList.vue"
import PProgress from "@/components/elements/PProgress.vue"
import PButton from "@/components/elements/PButton.vue"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import { FileRelation } from "@/models/file/file-relation.model"
import { useWindowStore } from "@/store/windows.store"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"

defineOptions({
  inheritAttrs: false
})

const ENTER_DELAY = 500
const LEAVE_DELAY = 500

export type ShowFileViewerPropOptions = "never" | "always" | number

const fileCollection = defineModel<FileRelation[]>({ required: true })
const attrs = useAttrs()
const attrsClass = computed(() => attrs.class as HTMLAttributes["class"])

const props = withDefaults(
  defineProps<{
    showFileViewer?: ShowFileViewerPropOptions
    isUploading?: boolean
    size?: "sm" | "md" | "lg"
    variant?: InstanceType<typeof PButton>["$props"]["variant"]
    /**
     * Whether to hide the file count. This file count is clickable and will open the file viewer.
     */
    hideFileCount?: boolean
    dragDefaultIsCopy?: boolean
    /**
     * CSS class to control the file preview alignment (e.g., 'justify-start' for left alignment)
     */
    previewClass?: string
    onDelete?: (fileRelation: FileRelation) => void
    onMove?: (fileRelation: FileRelation) => void
  }>(),
  {
    showFileViewer: "never",
    isUploading: false,
    hideFileCount: false,
    size: "sm",
    variant: undefined,
    previewClass: ""
  }
)

const windowStore = useWindowStore()

const layoutColumnsStore = useLayoutColumnsStore()

const { fileViewers } = storeToRefs(layoutColumnsStore)

const { windows } = storeToRefs(windowStore)

const fileCountButton = ref()

const isPreviewMode = ref(false)

const isActive = ref(false)

const isLoading = ref(false)

const hoverTimer = ref<number | null>(null)
const closeTimer = ref<number | null>(null)
const isMouseInPreview = ref(false)
const isContextMenuOpen = ref(false)

// Check if files is open in a Column or Window
watch(
  [fileViewers, () => windows.value],
  () => {
    isActive.value =
      fileViewers.value.some(
        (fileViewer) => fileViewer.props?.modelValue === fileCollection.value
      ) || windows.value.some((window) => window.props?.modelValue === fileCollection.value)
  },
  { deep: true }
)

const previewContainer = ref<HTMLDivElement | null>(null)

function disablePreviewMode() {
  isLoading.value = false
  isPreviewMode.value = false
}

function onMouseEnter() {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
  }
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
    closeTimer.value = null
  }
  hoverTimer.value = window.setTimeout(() => {
    if (fileCollection.value.length > 0) {
      isPreviewMode.value = true
    }
  }, ENTER_DELAY)
}

function onMouseLeave() {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
    hoverTimer.value = null
  }
  if (!isMouseInPreview.value && !isContextMenuOpen.value) {
    startCloseTimer()
  }
}

function onPreviewMouseEnter() {
  isMouseInPreview.value = true
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
    closeTimer.value = null
  }
}

/**
 * We use a custom leave emit to trigger this behaviour, the parent absolute div is bigger than the preview list.
 */
function onPreviewMouseLeave() {
  isMouseInPreview.value = false
  if (!isContextMenuOpen.value) {
    startCloseTimer()
  }
}

function startCloseTimer() {
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
  }
  closeTimer.value = window.setTimeout(() => {
    disablePreviewMode()
  }, LEAVE_DELAY)
}

async function onFileCountClick() {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
    hoverTimer.value = null
  }
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
    closeTimer.value = null
  }
  isLoading.value = true
  isPreviewMode.value = false
  isMouseInPreview.value = false

  if (fileCollection.value.length > 0) {
    try {
      await FileViewerController.display(fileCollection.value, fileCollection.value[0], {
        getArray: () => fileCollection.value,
        onDelete: props.onDelete
      })
    } finally {
      isLoading.value = false
    }
  }
}

// Clean up timers when component is unmounted
onUnmounted(() => {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
  }
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
  }
})
</script>

<style scoped></style>
